import type { Issue } from '../api/DigiobcanApi';
import { __ } from '../utils';
import {
  EMPTY_CELL,
  MeterBar,
  Stack,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import { differenceInCalendarDays } from 'date-fns';
import { formatDate } from 'date-fns';
import React from 'react';

/**
 * Stĺpec „Lehota" v zozname podnetov - dátum lehoty, zostávajúci čas a pás,
 * ktorý ukazuje, koľko z 30-dňovej lehoty už ubehlo.
 *
 * Farba je jediný nositeľ dôležitosti, preto ju drží jeden token na stav:
 * po lehote → error, blíži sa → warning, v pohode → tichý text,
 * vyriešené → disabled (už netlačí na čas).
 */
type Props = {
  /** Dátum podania podnetu - začiatok lehoty. */
  submittedAt?: string;
  /** Dátum, do ktorého sa má úrad vyjadriť. */
  deadlineAt?: string;
  /** Príznak z API: lehota uplynula a podnet je stále nevyriešený. */
  overdue?: boolean;
  /**
   * Nálezy podnetu. Rozhodujú o tom, či lehota ešte tlačí - viď
   * `deadlinePending`. Bez nich sa lehota sleduje ďalej.
   */
  issues?: { status: Issue['status'] }[];
};

/**
 * Stavy nálezu, pri ktorých lehota beží ďalej - všetko, čo ešte nie je
 * vyriešené. Prijatie lehotu **nezastaví** (moj-presov#3): prijatý nález je
 * rozrobená práca, nie odpoveď občanovi.
 *
 * Nie je to interpretácia, je to definícia z API: `overdue` tam znamená presne
 * „lehota uplynula **a** aspoň jeden nález je v `UNRESOLVED_ISSUE_STATUSES`"
 * (`lib/reports/status.ts` v digiobcan-api). Podnet bez takého nálezu už po
 * lehote byť nemôže, takže odpočet by pri ňom klamal - preto sa nekreslí.
 */
const UNRESOLVED_STATUSES: Issue['status'][] = ['open', 'accepted'];

/**
 * Pýta sa priamo nálezov, nie derivovaného stavu podnetu - tá istá otázka ako
 * `overdue` na API, takže sa s ním nerozíde, ani keď sa pravidlá derivácie
 * (napr. „prijatý nález vyhráva") znova pohnú.
 */
const deadlinePending = (issues?: { status: Issue['status'] }[]) =>
  !issues || issues.some((issue) => UNRESOLVED_STATUSES.includes(issue.status));

/** Počet dní, od ktorého sa lehota považuje za blížiacu sa. */
const SOON_THRESHOLD_DAYS = 7;

const dayWord = (n: number) => {
  if (n === 1) return __('deň');
  if (n < 5) return __('dni');
  return __('dní');
};

export type DeadlineState = {
  deadline: Date;
  /** Kalendárnych dní do lehoty; záporné číslo znamená po lehote. */
  days: number;
  /** Token farby, ktorou sa lehota hlási. */
  color: string;
  /** Krátky tvar do bunky tabuľky - `+11 d`, `dnes`, `5 d`. */
  shortRel: string;
  /** Veta do tooltipu a pod pás. */
  fullRel: string;
  /** Koľko z lehoty ubehlo, v percentách. `undefined`, keď nie je z čoho rátať. */
  pct?: number;
  overdue: boolean;
  /** Lehota už netlačí - úrad konal. */
  resolved: boolean;
  /** Do konca lehoty zostáva menej než `SOON_THRESHOLD_DAYS`. */
  soon: boolean;
};

/**
 * Jediné miesto, kde sa lehota prekladá na farbu, text a podiel.
 *
 * Rovnaké čísla ukazuje bunka zoznamu aj dlaždica v detaile podnetu
 * ([ReportEnvelope](../pages/administracia/podnety/ReportDetailAdminPage/ReportEnvelope.tsx)),
 * takže výpočet nesmie stáť na dvoch miestach: úradník ich vidí za sebou
 * a rozdiel v jednom dni by znamenal, že jednému z nich neverí.
 */
export const deadlineState = ({
  submittedAt,
  deadlineAt,
  overdue,
  issues,
}: Props): DeadlineState | undefined => {
  if (!deadlineAt) return undefined;

  const now = new Date();
  const deadline = new Date(deadlineAt);
  const days = differenceInCalendarDays(deadline, now);
  const resolved = !overdue && !deadlinePending(issues);
  const soon =
    !resolved && !overdue && days >= 0 && days <= SOON_THRESHOLD_DAYS;

  // Zámerne najtmavšie kroky, nie `main`. Na bielej má `error.main` kontrast
  // 3,81:1 a `warning.dark` len 2,29:1, čo je na 13px polotučnom texte málo
  // (AA žiada 4,5:1). `error.dark` dá 6,26:1. `warning.extraDark` dá 3,62:1 -
  // stále pod AA, ale je to najtmavšia jantárová, ktorú téma má.
  const color = resolved
    ? 'text.disabled'
    : overdue
      ? 'error.dark'
      : soon
        ? 'warning.extraDark'
        : 'text.secondary';

  let shortRel: string;
  let fullRel: string;
  if (resolved) {
    shortRel = EMPTY_CELL;
    fullRel = __('Podnet je vyriešený, lehota sa už nesleduje.');
  } else if (days < 0) {
    const n = -days;
    shortRel = `+${n} d`;
    fullRel = `${n} ${dayWord(n)} ${__('po lehote')}`;
  } else if (days === 0) {
    shortRel = __('dnes');
    fullRel = __('Lehota vyprší dnes.');
  } else {
    shortRel = `${days} d`;
    fullRel = `${__('Zostáva')} ${days} ${dayWord(days)}`;
  }

  // Bez dátumu podania nevieme, aká je lehota dlhá, takže pás vynecháme
  // radšej než aby ukazoval vymyslený podiel.
  const total = submittedAt
    ? differenceInCalendarDays(deadline, new Date(submittedAt))
    : 0;
  const elapsed = submittedAt
    ? differenceInCalendarDays(now, new Date(submittedAt))
    : 0;

  return {
    deadline,
    days,
    color,
    shortRel,
    fullRel,
    pct: total > 0 ? (elapsed / total) * 100 : undefined,
    overdue: !!overdue,
    resolved,
    soon,
  };
};

export const DeadlineCell: React.FC<Props> = (props) => {
  const state = deadlineState(props);

  if (!state) {
    return <Typography variant='body2'>{EMPTY_CELL}</Typography>;
  }

  const { deadline, color, shortRel, fullRel, pct } = state;

  return (
    <Tooltip title={fullRel}>
      <Stack sx={{ gap: 0.875, pr: 1.5, minWidth: 150 }}>
        <Stack
          direction='row'
          sx={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {formatDate(deadline, 'dd.MM.yyyy')}
          </Typography>
          <Typography
            variant='caption'
            sx={{ fontWeight: 600, color, whiteSpace: 'nowrap' }}
          >
            {shortRel}
          </Typography>
        </Stack>
        {pct !== undefined && (
          <MeterBar value={pct} color={color} label={fullRel} />
        )}
      </Stack>
    </Tooltip>
  );
};

export default DeadlineCell;
